import { useEffect, useRef, useState, useCallback } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUpload";

export function getEmbedSrc(provider, url) {
  if (!url) return null;
  try {
    const trimmed = url.trim();
    if (provider === "spotify") {
      const m = trimmed.match(/open\.spotify\.com\/(?:intl-[a-z]{2,3}\/)?(track|playlist|album|episode|show)\/([a-zA-Z0-9]+)/);
      if (m) return `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=fernebia&theme=0`;
    }
    if (provider === "apple") {
      if (trimmed.includes("music.apple.com")) return trimmed.replace(/music\.apple\.com/g, "embed.music.apple.com");
    }
  } catch { /* invalid */ }
  return null;
}

function getSpotifyUri(url) {
  if (!url) return null;
  const m = url.match(/open\.spotify\.com\/(?:intl-[a-z]{2,3}\/)?(track|playlist|album|episode|show)\/([a-zA-Z0-9]+)/);
  return m ? `spotify:${m[1]}:${m[2]}` : null;
}

let spotifyApiPromise = null;
function loadSpotifyIframeApi() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.SpotifyIframeApi) return Promise.resolve(window.SpotifyIframeApi);
  if (spotifyApiPromise) return spotifyApiPromise;
  spotifyApiPromise = new Promise((resolve) => {
    window.onSpotifyIframeApiReady = (IFrameAPI) => {
      window.SpotifyIframeApi = IFrameAPI;
      resolve(IFrameAPI);
    };
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://open.spotify.com/embed/iframe-api/v1";
    document.head.appendChild(s);
  });
  return spotifyApiPromise;
}

export default function MusicPlayer({ music }) {
  const m = music || {};
  const hasAudio = !!m.audio_url;
  const hasSpotify = m.provider === "spotify" && !!getSpotifyUri(m.url);
  const hasApple = m.provider === "apple" && !!getEmbedSrc("apple", m.url);
  const enabled = hasAudio || hasSpotify || hasApple;

  const [muted, setMuted] = useState(true);
  const [showHint, setShowHint] = useState(false);
  const [everUnmuted, setEverUnmuted] = useState(false);
  const audioRef = useRef(null);
  const spotifyControllerRef = useRef(null);
  const spotifyPlayingRef = useRef(false);
  const removeGestureRef = useRef(null);
  // True once the user explicitly taps the toggle/hint. From then on the global auto-unmute
  // listeners must never touch the muted state again — this kills the iOS bug where a tap
  // unmutes but a trailing same-gesture event (or a rejected fallback play) re-mutes it,
  // forcing 2-3 taps.
  const userActedRef = useRef(false);

  // Once the music is unmuted by ANY means (button, hint or a page gesture), stop the global
  // auto-unmute listeners so a later manual mute isn't undone by the next scroll/tap.
  useEffect(() => {
    if (!muted && removeGestureRef.current) {
      removeGestureRef.current();
      removeGestureRef.current = null;
    }
  }, [muted]);

  // Once the music has been activated (unmuted) at least once, never nudge again — not on a
  // later manual mute, and not when the song ends (no loop).
  useEffect(() => {
    if (!muted) setEverUnmuted(true);
  }, [muted]);

  // Show a gentle "tap to play" hint ONLY on the initial load, while still muted and before the
  // user has ever activated it. On Android the browser blocks starting audio from a scroll/swipe
  // (only a real tap works), so we show it sooner (2s). On iOS/desktop the first interaction
  // usually unmutes on its own, so we wait longer (10s) before nudging.
  useEffect(() => {
    if (!enabled || everUnmuted || !muted) { setShowHint(false); return; }
    const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
    const delay = isAndroid ? 3000 : 15000;
    const t = setTimeout(() => setShowHint(true), delay);
    return () => clearTimeout(t);
  }, [enabled, muted, everUnmuted]);

  // AUDIO path: attempt to autoplay ASAP; on first user gesture, unmute synchronously (Chrome compat)
  useEffect(() => {
    if (!hasAudio) return;
    const el = audioRef.current;
    if (!el) return;

    el.loop = false;
    el.volume = 0.85;
    el.muted = true;
    setMuted(true);

    // Force load so Chrome starts fetching audio data early
    try { el.load(); } catch { /* ignore */ }

    // Kick off muted autoplay right away (always allowed)
    const kickPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* browser hard-blocked even muted */ });
    };
    kickPlay();

    // When the song finishes, it does NOT loop — reflect the stopped state in the UI.
    // iOS can fire a spurious `ended` right after unmuting mid-song; only honor it when the
    // audio is genuinely at/near its end, otherwise it would wrongly re-mute (the 2-tap bug).
    const onEnded = () => {
      const a = audioRef.current;
      if (a && a.duration && isFinite(a.duration) && a.currentTime < a.duration - 0.5) return;
      setMuted(true);
    };
    el.addEventListener("ended", onEnded);

    // First user gesture → unmute + play (MUST be synchronous inside the gesture handler).
    let handled = false;
    const removeListeners = () => {
      document.removeEventListener("pointerdown", enableSound, true);
      document.removeEventListener("pointerup", enableSound, true);
      document.removeEventListener("mousedown", enableSound, true);
      document.removeEventListener("click", enableSound, true);
      document.removeEventListener("touchstart", enableSound, true);
      document.removeEventListener("touchend", enableSound, true);
      document.removeEventListener("keydown", enableSound, true);
      document.removeEventListener("scroll", enableSound, true);
    };
    const enableSound = (ev) => {
      if (handled || !audioRef.current) return;
      // The user already took explicit control via the button/hint — never auto-touch again.
      if (userActedRef.current) { removeListeners(); return; }
      // Let the explicit music button and hint handle their own tap via onClick. Otherwise the
      // global touchstart-initiated play() would (on iOS) be rejected and its catch would re-mute
      // right over the button's successful unmute — forcing the user to tap 2-3 times.
      try {
        const t = ev && ev.target;
        if (t && typeof t.closest === "function" &&
            t.closest('[data-testid="music-toggle"], [data-testid="music-hint"]')) return;
      } catch { /* ignore */ }
      const a = audioRef.current;
      // Unmuting + play() must be called synchronously here so the browser's transient
      // user-activation is still valid. We do NOT gate with an "attempting" flag: on Samsung/
      // Android a scroll is bracketed by touchstart...touchend and we want EVERY activation
      // event to get a shot (a pending promise must not block a later touchend).
      a.muted = false;
      const p = a.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          if (a.muted) return; // this resolution was for a muted fallback play — ignore
          handled = true;
          setMuted(false);
          removeListeners();
        }).catch((err) => {
          // AbortError = a newer play() interrupted this one (benign for rapid same-gesture
          //   events like pointerdown→touchstart→click) → ignore, the newer call wins.
          // NotAllowedError = the browser blocked UNMUTED playback for this event (e.g. a
          //   scroll that lost activation) → fall back to muted playback and keep listening.
          if (err && err.name === "NotAllowedError") {
            if (userActedRef.current) return; // user already unmuted explicitly — don't fight it
            a.muted = true;
            setMuted(true);
            const p2 = a.play();
            if (p2 && typeof p2.catch === "function") p2.catch(() => {});
          }
        });
      } else {
        handled = true;
        setMuted(false);
        removeListeners();
      }
    };
    // Capture-phase so we run before any child handler; NO async.
    // A SCROLL gesture (not just a tap) is bracketed by touchstart...touchend — both are
    // activation events — so listening to both lets a scroll-first interaction unmute too.
    document.addEventListener("pointerdown", enableSound, true);
    document.addEventListener("pointerup", enableSound, true);
    document.addEventListener("mousedown", enableSound, true);
    document.addEventListener("click", enableSound, true);
    document.addEventListener("touchstart", enableSound, true);
    document.addEventListener("touchend", enableSound, true);
    document.addEventListener("keydown", enableSound, true);
    document.addEventListener("scroll", enableSound, true);
    removeGestureRef.current = removeListeners;

    return () => {
      removeListeners();
      removeGestureRef.current = null;
      el.removeEventListener("ended", onEnded);
    };
  }, [hasAudio]);

  // SPOTIFY path: hidden iframe + iFrame API.
  // Rules:
  //  - We ONLY call play() on a user gesture (Chrome blocks iframe autoplay without one).
  //  - We call play() only if track hasn't started yet OR if it finished naturally.
  //  - While a track is playing, gestures are IGNORED (don't restart mid-song).
  //  - The mute button's own click manages pause/resume via togglePlay() — we exclude it from the global handler.
  useEffect(() => {
    if (!hasSpotify) return;
    let mounted = true;
    const container = document.getElementById("fn-spotify-embed");
    if (!container) return;

    // Playback state flags (mutable refs inside the effect closure)
    let controller = null;
    let hasStarted = false;  // has the user ever triggered play?
    let hasEnded = false;    // did the track finish naturally?
    let lastDuration = 0;
    let sawNearEnd = false;  // did we recently observe position near the end (Spotify may reset pos to 0 on end)
    let lastPos = 0;

    const tryPlayIfIdle = () => {
      if (!controller) return;
      if (hasStarted && !hasEnded) return; // song is playing — DO NOT interrupt
      try {
        if (hasEnded) controller.seek(0);
        controller.play();
        hasStarted = true;
        hasEnded = false;
        sawNearEnd = false;
      } catch { /* controller not ready or blocked */ }
    };

    loadSpotifyIframeApi().then((IFrameAPI) => {
      if (!mounted) return;
      const uri = getSpotifyUri(m.url);
      IFrameAPI.createController(container, { uri, width: 300, height: 80 }, (EmbedController) => {
        if (!mounted) return;
        controller = EmbedController;
        spotifyControllerRef.current = EmbedController;

        EmbedController.addListener("playback_update", (e) => {
          const d = e && e.data ? e.data : {};
          const isPaused = !!d.isPaused;
          const pos = d.position || 0;
          const dur = d.duration || 0;
          if (dur > 0) lastDuration = dur;

          // Detect "near the end" once — Spotify may then reset position to 0 or pause here
          if (hasStarted && lastDuration > 0 && pos >= lastDuration - 1500) {
            sawNearEnd = true;
          }

          // A track ended if:
          //   (a) we saw it reach near the end AND it's now paused, OR
          //   (b) position dropped from near-end back down (Spotify auto-reset to 0 at end)
          const droppedFromEnd = sawNearEnd && lastPos > pos + 5000; // position went DOWN by >5s → wrap
          if (hasStarted && ((sawNearEnd && isPaused) || droppedFromEnd)) {
            hasEnded = true;
          }

          // Fresh playback from beginning resets the flags
          if (!isPaused && pos > 500 && pos < lastDuration - 5000) {
            hasEnded = false;
            sawNearEnd = false;
          }

          lastPos = pos;
          // Sync UI muted state with real playback
          spotifyPlayingRef.current = !isPaused;
          setMuted(isPaused);
        });
      });
    });

    // Any user gesture triggers play — but only if track is idle (never started, or ended)
    const handleGesture = (ev) => {
      // Ignore clicks on the mute toggle button (it handles pause/resume itself)
      try {
        const t = ev && ev.target;
        if (t && typeof t.closest === "function" && t.closest('[data-testid="music-toggle"]')) return;
      } catch { /* ignore */ }
      tryPlayIfIdle();
    };
    document.addEventListener("pointerdown", handleGesture, true);
    document.addEventListener("click", handleGesture, true);
    document.addEventListener("touchstart", handleGesture, { capture: true, passive: true });
    document.addEventListener("scroll", handleGesture, true);

    return () => {
      mounted = false;
      document.removeEventListener("pointerdown", handleGesture, true);
      document.removeEventListener("click", handleGesture, true);
      document.removeEventListener("touchstart", handleGesture, true);
      document.removeEventListener("scroll", handleGesture, true);
    };
  }, [hasSpotify, m.url]);

  const toggle = useCallback(() => {
    // Explicit user action wins over everything. Detach the global auto-unmute listeners
    // synchronously (not via effect) so no trailing same-gesture event can re-mute on iOS.
    userActedRef.current = true;
    if (removeGestureRef.current) { removeGestureRef.current(); removeGestureRef.current = null; }
    if (hasAudio && audioRef.current) {
      const el = audioRef.current;
      // "On" means actually audible: unmuted AND playing.
      const isOn = !el.muted && !el.paused;
      // iOS/iPadOS Safari unloads a PAUSED streamed <audio> and reloads it from 0 on the next
      // play() — that was the "se rebobina y hay que tocar 2 veces" bug (first tap reloads,
      // second actually plays). So on iOS we never pause: we just flip `muted` while the element
      // keeps playing, making unmute instant, single-tap and without any rewind.
      const isIOS = typeof navigator !== "undefined" &&
        (/iP(hone|od|ad)/.test(navigator.userAgent || "") ||
          (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
      if (isIOS) {
        if (!isOn) {
          el.muted = false;
          if (el.paused) { const p = el.play(); if (p && typeof p.catch === "function") p.catch(() => {}); }
          setMuted(false);
        } else {
          el.muted = true; // keep playing silently — do NOT pause on iOS
          setMuted(true);
        }
        return;
      }
      if (!isOn) {
        // ACTIVAR — NO rebobinar en una reanudación normal. Solo reiniciar desde 0 si terminó.
        if (el.ended) { try { el.currentTime = 0; } catch { /* ignore */ } }
        el.muted = false;
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => { /* browser blocked */ });
        setMuted(false);
      } else {
        // SILENCIAR — pausa (sin rebobinar); al reactivar continúa donde quedó.
        el.muted = true;
        try { el.pause(); } catch { /* ignore */ }
        setMuted(true);
      }
      return;
    }
    if (hasSpotify && spotifyControllerRef.current) {
      try { spotifyControllerRef.current.togglePlay(); spotifyPlayingRef.current = !spotifyPlayingRef.current; setMuted(!spotifyPlayingRef.current); } catch { /* controller not ready */ }
      return;
    }
    setMuted((v) => !v);
  }, [hasAudio, hasSpotify]);

  if (!enabled) return null;

  return (
    <>
      {hasAudio && (
        <audio
          ref={audioRef}
          src={resolveImageUrl(m.audio_url)}
          autoPlay
          muted
          preload="auto"
          crossOrigin="anonymous"
          style={{ display: "none" }}
          data-testid="music-audio"
        />
      )}
      {hasSpotify && (
        <div style={{ position: "fixed", left: -9999, top: -9999, width: 1, height: 1, overflow: "hidden", opacity: 0, pointerEvents: "none" }} aria-hidden>
          <div id="fn-spotify-embed" />
        </div>
      )}
      {hasApple && (
        <iframe
          title="apple-embed-hidden"
          src={getEmbedSrc("apple", m.url)}
          width="1"
          height="1"
          style={{ position: "fixed", left: -9999, top: -9999, width: 1, height: 1, overflow: "hidden", opacity: 0, pointerEvents: "none" }}
          allow="autoplay; encrypted-media"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          aria-hidden
        />
      )}
      {showHint && muted && !everUnmuted && (
        <button
          onClick={toggle}
          data-testid="music-hint"
          aria-label="Activar música"
          style={{
            position: "fixed",
            bottom: "2.1rem",
            right: "5.25rem",
            zIndex: 2147483000,
            maxWidth: "min(62vw, 230px)",
            padding: "0.5rem 0.9rem",
            borderRadius: "9999px",
            background: "rgba(28, 24, 22, 0.92)",
            color: "#F5EFE6",
            border: "1px solid rgba(197, 168, 128, 0.55)",
            boxShadow: "0 8px 24px rgba(62, 58, 53, 0.3)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            fontSize: "12px",
            lineHeight: 1.2,
            letterSpacing: "0.02em",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            whiteSpace: "nowrap",
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
          }}
          className="fn-hint-blink"
        >
          <Volume2 size={15} color="#C5A880" />
          <span>Toca para activar la música</span>
        </button>
      )}
      <button
        onClick={toggle}
        data-testid="music-toggle"
        aria-label={muted ? "Activar música" : "Silenciar música"}
        style={{
          position: "fixed",
          bottom: "1.5rem",
          right: "1.5rem",
          zIndex: 2147483000,
          width: "56px",
          height: "56px",
          borderRadius: "9999px",
          display: "grid",
          placeItems: "center",
          background: "rgba(255, 255, 255, 0.92)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          border: "1px solid rgba(197, 168, 128, 0.55)",
          boxShadow: "0 10px 30px rgba(62, 58, 53, 0.25), 0 0 0 4px rgba(197, 168, 128, 0.15)",
          cursor: "pointer",
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        }}
        className="hover:scale-105 transition-transform"
      >
        {muted ? (
          <>
            <VolumeX size={24} color="#3E3A35" style={{ position: "relative", zIndex: 10 }} data-testid="icon-muted" />
            <span style={{ position: "absolute", inset: 0, borderRadius: "9999px", boxShadow: "0 0 0 2px rgba(197, 168, 128, 0.6) inset" }} className="animate-ping" aria-hidden />
          </>
        ) : (
          <>
            <Volume2 size={24} color="#C5A880" style={{ position: "relative", zIndex: 10 }} data-testid="icon-playing" />
            <span style={{ position: "absolute", inset: 0, borderRadius: "9999px", boxShadow: "0 0 0 2px rgba(197, 168, 128, 0.5) inset" }} className="animate-ping" aria-hidden />
          </>
        )}
      </button>
    </>
  );
}
